import { unwrapResult } from "@reduxjs/toolkit";
import {
  Col,
  Form,
  Input,
  Modal,
  ModalProps,
  notification,
  Radio,
  Row,
  Select,
} from "antd";
import cnchar from "cnchar";
import _ from "lodash";
import React, { ReactElement, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { RootDispatch } from "../../app/store";
import { Department } from "../../models/department";
import {
  Gender,
  GenderOptions,
  getGenderName,
  getStationCategoryName,
  getStationPropertyName,
  Role,
  StationCategories,
  StationCategory,
  StationProperties,
  StationProperty,
} from "../../models/user";
import { getFullDeparmentList } from "../completion/completionSlice";
import { createOrUpdateUser, getUserDetail } from "./userSlice";
interface UserModalProps {
  userId?: string;
  tenantId?: string;
}

export const UserModal = ({
  userId,
  tenantId,
  onCancel,
  onOk,
  ...others
}: UserModalProps & ModalProps): ReactElement => {
  const dispatch = useDispatch<RootDispatch>();

  const [form] = Form.useForm();

  const [isDoctor, setIsDoctor] = useState(true);

  const [detailList, setDetailList] = useState<any>();

  const [departments, setDepartments] = useState<Department[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(
      getFullDeparmentList({ current: 1, size: 1000, status: 1, tenantId })
    )
      .then(unwrapResult)
      .then((departments) => setDepartments(departments));
  }, [dispatch]);


  useEffect(() => {
    if (userId) {
      dispatch(getUserDetail({ userId, tenantId }))
        .then(unwrapResult)
        .then((user) => {
          setIsDoctor(
            user.stationCategory === StationCategory.DoctorOrPharmacist
          );
          setDetailList(user);
          form.setFieldsValue({
            ...user,
            departmentId: user?.innerDepartmentName,
            outpatientId: user?.outpatientDepartmentName,
          });
        });
    } else {
      setIsDoctor(true);
      form.resetFields();
    }
  }, [dispatch, userId]);

  const layout = {
    labelCol: {
      span: 5,
    },
    wrapperCol: {
      span: 19,
    },
  };

  return (
    <Modal
      {...others}
      title={`${userId ? "编辑" : "新增"}员工`}
      width={900}
      confirmLoading={isLoading}
      cancelText="取消"
      okText="确定"
      onCancel={(e) => {
        onCancel && onCancel(e);
        form.resetFields();
      }}
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            const reg = /^[0-9]+.?[0-9]*$/;
            setIsLoading(true);

            dispatch(
              createOrUpdateUser({
                user: {
                  ...values,
                  role: tenantId ? values.role : Role.User,
                  state: 0,
                  departmentId:
                    reg.test(values.departmentId)
                      ? values.departmentId
                      : reg.test(detailList?.departmentId) 
                      ? detailList?.departmentId
                      : null,
                  outpatientId:
                    reg.test(values.outpatientId) 
                      ? values.outpatientId
                      : reg.test(detailList?.outpatientId) 
                      ? detailList?.outpatientId
                      : null,
                  departmentName: _.chain(departments)
                    .find((d) => d.id === values.departmentId)
                    .value()?.name,
                  outpatientName: _.chain(departments)
                    .find((d) => d.id === values.outpatientId)
                    .value()?.name,
                  id: userId,
                },
                tenantId,
              })
            )
              .then(unwrapResult)
              .then(() => {
                setIsLoading(false);
                setIsDoctor(true);
                form.resetFields();
                notification.success({
                  message: userId ? "修改员工成功" : "新建员工成功",
                });

                onOk && onOk(userId ? '' : values.name);
              })
              .catch(() => setIsLoading(false));
          })
          .catch(() => {
            // do nothing.
          });
      }}
    >
      <Form
        form={form}
        autoComplete="off"
        colon={false}
        initialValues={{
          gender: Gender.Male,
          stationCategory: StationCategory.DoctorOrPharmacist,
          stationProperty: StationProperty.OnJob,
          role: tenantId ? Role.Manager : Role.User,
          roleFilter: 0
        }}
        labelAlign="right"
      >
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              {...layout}
              name="name"
              label="姓名"
              required
              rules={[
                { required: true, message: "姓名不能为空,请输入正确的值。" },
                { max: 40, message: "最长40位!" },
              ]}
            >
              <Input
                onChange={(e) => {
                  form.setFieldsValue({
                    mnemonicCode: cnchar.spell(e.target.value, "first"),
                  });
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              {...layout}
              name="gender"
              label="性别"
              required
              rules={[
                { required: true, message: "性别不能为空,请输入正确的值。" },
              ]}
            >
              <Radio.Group>
                {GenderOptions.map((g) => (
                  <Radio key={g} value={g}>
                    {getGenderName(g)}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              {...layout}
              name="mnemonicCode"
              label="助记码"
              required
              rules={[
                { required: true, message: "助记码不能为空,请输入正确的值。" },
                { max: 40, message: "最长40位!" },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              {...layout}
              name="phone"
              label="手机号"
              required
              rules={[
                { required: true, message: "手机号不能为空,请输入正确的值。" },
                {
                  pattern: /^5|1[3|4|5|6|7|8|9]\d{9}$/,
                  message: "请输入正确的手机号",
                },

                {
                  max: 11,
                  message: "最长11位!",
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              {...layout}
              name="identityCard"
              label="身份证号"
              rules={[
                {
                  pattern: /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/,
                  message: "你的身份证格式不正确",
                },
                {
                  max: 18,
                  message: "最长18位!",
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          {userId ? (
            <Col span={12}>
              <Form.Item {...layout} name="password" label="密码" required>
                <Input disabled />
              </Form.Item>
            </Col>
          ) : (
            <Col span={12}>
              <Form.Item
                {...layout}
                name="password"
                label="密码"
                required
                rules={[
                  { required: true, message: "密码不能为空,请输入正确的值。" },
                  {
                    pattern: /^[^\s]*$/,
                    message: "密码不能存在空格",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          )}
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              {...layout}
              name="departmentId"
              label="所属科室"
              required
              rules={[
                {
                  required: true,
                  message: "所属科室不能为空,请输入正确的值。",
                },
              ]}
            >
              <Select>
                {departments.map((d) => (
                  <Select.Option key={d.id} value={d.id}>
                    {d.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              {...layout}
              name="outpatientId"
              label="门诊科室"
              required
              rules={[
                {
                  required: true,
                  message: "门诊科室不能为空,请输入正确的值。",
                },
              ]}
            >
              <Select>
                {departments.map((d) => (
                  <Select.Option key={d.id} value={d.id}>
                    {d.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              {...layout}
              name="stationCategory"
              label="岗位类别"
              required
              rules={[
                {
                  required: true,
                  message: "岗位类别不能为空,请输入正确的值。",
                },
              ]}
            >
              <Select
                onChange={(v) => {
                  setIsDoctor(v === StationCategory.DoctorOrPharmacist);
                }}
              >
                {StationCategories.map((c) => (
                  <Select.Option key={c} value={c}>
                    {getStationCategoryName(c)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              {...layout}
              name="stationProperty"
              label="岗位性质"
              required
              rules={[
                {
                  required: true,
                  message: "岗位性质不能为空,请输入正确的值。",
                },
              ]}
            >
              <Select value={StationProperty.OnJob}>
                {StationProperties.map((p) => (
                  <Select.Option key={p} value={p}>
                    {getStationPropertyName(p)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        {isDoctor && (
          <>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  {...layout}
                  name="doctorCode"
                  label="医师编号"
                  required
                  rules={[
                    {
                      required: true,
                      message: "医师编号不能为空,请输入正确的值。",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
                <Col span={12}>
                    <Form.Item
                        {...layout}
                        name='jobTitle'
                        label='医生职称'
                    >
                        <Select>
                            <Select.Option value={'助理执业医师'}>助理执业医师</Select.Option>
                            <Select.Option value={'执业医师'}>执业医师</Select.Option>
                            <Select.Option value={'主治医师'}>主治医师</Select.Option>
                            <Select.Option value={'副主任医师'}>副主任医师</Select.Option>
                            <Select.Option value={'主任医师'}>主任医师</Select.Option>
                        </Select>
                    </Form.Item>
                </Col>

            </Row>
            <Row gutter={24}>
                <Col span={12}>
                    <Form.Item
                        {...layout}
                        label="执业证书"
                        name="practiceCertificate"
                        rules={[{ max: 250, message: "最长250位!" }]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
              <Col span={12}>
                <Form.Item
                  {...layout}
                  label="资格证书"
                  name="qualificationCertificate"
                  rules={[{ max: 250, message: "最长250位!" }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item {...layout} name="countryCode" label="国家编码">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item {...layout} name="email" label="电子邮件">
                  <Input />
                </Form.Item>
              </Col>

            </Row>
          </>
        )}
          <Row gutter={24}>
          <Col span={12}>
              <Form.Item
                  {...layout}
                  name="roleFilter"
                  label="顶级管理员"
                  required
                  rules={[
                      { required: true, message: "顶级管理员不能为空！" },
                  ]}
              >
                  <Radio.Group>
                      <Radio value={1}>否</Radio>
                      <Radio value={0}>是</Radio>
                  </Radio.Group>
              </Form.Item>
          </Col>
        {tenantId && (

            <Col span={12}>
              <Form.Item label="是否管理员" name="role">
                <Radio.Group>
                  <Radio value={Role.Manager}>是</Radio>
                  <Radio value={Role.User}>否</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>

        )}
          </Row>
      </Form>
    </Modal>
  );
};
